import { useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import {
  GET_REVIEW_STATS,
  GET_PENDING_SUBMISSIONS,
  GET_PENDING_WITHDRAWALS,
} from "./api/review";

const ReviewsDashboard = () => {
  const navigate = useNavigate();

  const { data: statsData, loading: statsLoading } = useQuery(
    GET_REVIEW_STATS,
    {
      pollInterval: 30000, // 30초마다 업데이트
    }
  );

  const { data: submissionsData, loading: submissionsLoading } = useQuery(
    GET_PENDING_SUBMISSIONS,
    {
      variables: { filter: {} },
      fetchPolicy: "network-only",
    }
  );

  const { data: withdrawalsData, loading: withdrawalsLoading } = useQuery(
    GET_PENDING_WITHDRAWALS,
    {
      variables: { filter: {} },
      fetchPolicy: "network-only",
    }
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (statsLoading) {
    return <div className="text-center py-8">로딩 중...</div>;
  }

  const stats = statsData?.missionAdminReviewStats || {
    pendingSubmissionsCount: 0,
    pendingWithdrawalsCount: 0,
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">검수 관리</h1>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div
          className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/ops/mission/reviews/submissions")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-2">미션 인증검수</p>
              <p className="text-3xl font-bold text-blue-600">
                {stats.pendingSubmissionsCount}
              </p>
            </div>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/ops/mission/reviews/successes")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-2">미션 성공처리</p>
              <p className="text-3xl font-bold text-emerald-600">-</p>
            </div>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/ops/mission/reviews/failures")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-2">미션 실패처리</p>
              <p className="text-3xl font-bold text-red-600">-</p>
            </div>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          className="bg-white p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/ops/mission/reviews/withdrawals")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm mb-2">대기 중인 출금 요청</p>
              <p className="text-3xl font-bold text-green-600">
                {stats.pendingWithdrawalsCount}
              </p>
            </div>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* 최근 대기 항목 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 최근 미션 제출 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">최근 미션 제출</h2>
          </div>
          <div className="p-4">
            {submissionsLoading ? (
              <div className="text-center py-4">로딩 중...</div>
            ) : submissionsData?.missionAdminPendingSubmissions?.length > 0 ? (
              <div className="space-y-3">
                {submissionsData.missionAdminPendingSubmissions
                  .slice(0, 5)
                  .map((submission: any) => (
                    <div
                      key={submission.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/ops/mission/reviews/submissions/${submission.id}`
                        )
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {submission.participation.run.campaign.productName}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {submission.participation.user.name ||
                              `유저 ${submission.participation.user.id}`}{" "}
                            · {submission.step.title}
                          </p>
                        </div>
                        <div className="text-xs text-gray-400">
                          {submission.submittedAt
                            ? formatDate(submission.submittedAt)
                            : "-"}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                대기 중인 미션 제출이 없습니다.
              </div>
            )}
            {submissionsData?.missionAdminPendingSubmissions?.length > 5 && (
              <div className="mt-4 text-center">
                <Link
                  to="/ops/mission/reviews/submissions"
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  전체 보기 →
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* 최근 출금 요청 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">최근 출금 요청</h2>
          </div>
          <div className="p-4">
            {withdrawalsLoading ? (
              <div className="text-center py-4">로딩 중...</div>
            ) : withdrawalsData?.missionAdminPendingWithdrawals?.length > 0 ? (
              <div className="space-y-3">
                {withdrawalsData.missionAdminPendingWithdrawals
                  .slice(0, 5)
                  .map((withdrawal: any) => (
                    <div
                      key={withdrawal.id}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        navigate(
                          `/ops/mission/reviews/withdrawals/${withdrawal.id}`
                        )
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {withdrawal.user.name ||
                              `유저 ${withdrawal.user.id}`}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {withdrawal.amountRequested.toLocaleString()}원
                          </p>
                        </div>
                        <div className="text-xs text-gray-400">
                          {withdrawal.requestedAt
                            ? formatDate(withdrawal.requestedAt)
                            : "-"}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                대기 중인 출금 요청이 없습니다.
              </div>
            )}
            {withdrawalsData?.missionAdminPendingWithdrawals?.length > 5 && (
              <div className="mt-4 text-center">
                <Link
                  to="/ops/mission/reviews/withdrawals"
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  전체 보기 →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsDashboard;
